import { useEffect, useRef } from "react";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setupAuthListener } from "./utils/firebase/firebase.utils.js";
import { selectAuthIsReady, selectUser, selectEmailVerificationPending } from "./store/user/user.selector.js";
import { useUserTeamIndexListener } from "./hooks/useUserTeamIndexListener.js";

// components


// pages
import AuthPage from "./pages/auth-page/AuthPage";
import LandingPage from "./pages/landing-page/LandingPage.js";
import SiteContainer from "./pages/site-container/SiteContainer.js";
import HomePage from "./pages/home-page/HomePage";

import ShowcaseContainer from './pages/showcase/ShowcaseContainer.js';
import ShowcaseDisplay from "./pages/showcase/components/showcase-display/ShowcaseDisplay.js";
import ShowcaseSelectionView from "./pages/showcase/components/card-selection-view/CardSelectionView.js";
import SeriesSelection from "./pages/checklist-page/components/series-selection/SeriesSelection.js";
import ChecklistHeader from "./pages/checklist-page/components/checklist-header/ChecklistHeader.js";
import ChecklistDisplay from "./pages/checklist-page/components/checklist-display/ChecklistDisplay.js";
import ChecklistCardListView from "./pages/checklist-page/components/checklist-display/views/checklist-card-list/ChecklistCardListView.js";

import PSAPage from "./pages/psa/PSAPage.js";
import FriendsPage from "./pages/friends-page/FriendsPage.js";
import TokenPage from "./pages/token-page/TokenPage.js";
import ChecklistPage from "./pages/checklist-page/ChecklistPage.js";


import SetsPage from "./pages/sets-page/SetsPage.js";
import SalesPage from "./pages/sales-page/SalesPage.js";
import SalesDashboard from "./pages/sales-page/components/sales-dashboard/SalesDashboard.js";
import SalesTracker from "./pages/sales-page/components/sales-tracker/SalesTracker.js";
import ListingDetailPage from "./pages/sales-page/components/sales-tracker/components/listing-detail/ListingDetailPage.js";
import SalesInventory from "./pages/sales-page/components/sales-inventory/SalesInventory.js";
import TransactionQueue from "./pages/sales-page/components/transaction-queue/TransactionQueue.js";
import AccountPage from "./pages/account-page/AccountPage";
import ActivityLog from "./pages/activity-log/ActivityLog.js";
import AdminDashboard from "./pages/admin-dashboard/AdminDashboard.js";
import WhatnotPage from "./pages/whatnot/whatnot-backend/WhatnotPage.js";
import ShowHost from "./pages/show/host/ShowHost.js";
import ShowViewer from "./pages/show/viewer/ShowViewer.js";
import CardCropperPage from "./pages/card-cropper/CardCropperPage.js";
import MlbTheShowPage from "./pages/mlb-the-show/MlbTheShowPage.js";

import SetDetailPage from "./pages/sets-page/components/set-detail/SetDetailPage.js";














import FriendCollectionPage from "./pages/friends-page/components/friend-collection-page/FriendCollectionPage.js";
import FriendSeriesDisplay from "./pages/friends-page/components/friend-series-display/FriendSeriesDisplay.js";
import FriendListDisplay from "./pages/friends-page/components/friend-list-display/FriendListDisplay.js";

import RouteNotFound from "./pages/route-not-found/RouteNotFound.js";
import AdminRoute from "./components/AdminRoute/AdminRoute.js";

function LegacySalesRedirect() {
  const { pathname, search } = useLocation();
  const next = pathname.replace(/^\/sales/, '/transactions') || '/transactions/dashboard';
  return <Navigate to={`${next}${search}`} replace />;
}

function App() {
  useUserTeamIndexListener();
  const dispatch = useDispatch();
  const authIsReady = useSelector(selectAuthIsReady);
  const user = useSelector(selectUser);
  const emailVerificationPending = useSelector(selectEmailVerificationPending);
  const authListenerSet = useRef(false);

  useEffect(() => {
    if (!authListenerSet.current) {
      dispatch(setupAuthListener());
      authListenerSet.current = true;
    }
  }, [dispatch]);

  return (
    <div>
      {authIsReady &&
        <BrowserRouter>
          <Routes>
            <Route path='/auth' element={
              (user && !emailVerificationPending) ? <Navigate to="/" replace /> : <AuthPage />
            } />
            <Route path='/' element={
              emailVerificationPending ? <Navigate to="/auth" replace /> :
              (user ? <SiteContainer /> : <LandingPage />)
            }>
              <Route path='' element={<HomePage />}>
                <Route path='' element={<ShowcaseContainer />}>
                  <Route path='' element={<ShowcaseDisplay />} />
                  <Route path='selection-view' element={<ShowcaseSelectionView />} />
                </Route>
              </Route>
              
              <Route path='checklists' element={<ChecklistPage />}>
                <Route path=':sport/:year/:manufacturer' element={<SeriesSelection />} />
                <Route path=':sport/:year/:manufacturer/:series' element={<ChecklistHeader />}>
                  <Route path=':cardType/:cardTypeCategory' element={<ChecklistDisplay />}>
                    <Route index element={<ChecklistCardListView />} />
                    <Route path="checklist" element={<Navigate to=".." replace />} />
                    <Route path="team" element={<Navigate to=".." replace />} />
                  </Route>
                </Route>
              </Route>

              <Route path='activity-log' element={<ActivityLog />} />
              <Route path='account' element={<AccountPage />} />
              <Route path='admin-dashboard' element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path='tokens' element={<TokenPage />} />
              <Route path='sets' element={<AdminRoute><SetsPage /></AdminRoute>} />
              <Route path='sets/:setSlug' element={<AdminRoute><SetDetailPage /></AdminRoute>} />

              <Route path='transactions' element={<AdminRoute><SalesPage /></AdminRoute>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path='dashboard' element={<SalesDashboard />} />
                <Route path='queue' element={<TransactionQueue />} />
                <Route path='tracker' element={<SalesTracker />} />
                <Route path='tracker/:saleId' element={<ListingDetailPage />} />
                <Route path='inventory' element={<SalesInventory />} />
              </Route>
              <Route path='sales/*' element={<LegacySalesRedirect />} />
              <Route path='ebay' element={<Navigate to='/transactions/queue?platform=eBay' replace />} />

              <Route path='whatnot' element={<AdminRoute><WhatnotPage /></AdminRoute>} />
              <Route path='show/:showId/host' element={<AdminRoute><ShowHost /></AdminRoute>} />
              <Route path='psa' element={<AdminRoute><PSAPage /></AdminRoute>} />
              <Route path='friends' element={<AdminRoute><FriendsPage /></AdminRoute>} />
              <Route path='friends/:friendUID/collection' element={<AdminRoute><FriendCollectionPage /></AdminRoute>}>
                <Route path=':sport/:year/:manufacturer' element={<SeriesSelection viewing={false} />} />
                <Route path=':sport/:year/:manufacturer/:series' element={<FriendListDisplay />}>
                  <Route path=':cardType/:cardTypeCategory' element={<FriendSeriesDisplay />} />
                </Route>
              </Route>
              
            </Route>

            {/* Standalone viewer window — no SiteContainer chrome */}
            <Route path='/show/:hostUid/:showId/viewer' element={<ShowViewer />} />
            <Route path='/card-cropper' element={<CardCropperPage />} />
            <Route path='/mlb-the-show' element={<MlbTheShowPage />} />

            {/* Catch-all route for 404 pages */}
            <Route path='*' element={<RouteNotFound />} />

          </Routes>
        </BrowserRouter>
      }
    </div>
  )
}

export default App;
